import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useApp } from "./context";
import { t } from "./i18n";

export function AdminLogin() {
  const { lang, login, user } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user?.role === "admin") return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault(); setErr(null); setLoading(true);
    try { await login(email, password); navigate("/admin/dashboard"); }
    catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-secondary)' }} data-testid="page-admin-login">
      <form onSubmit={submit} className="w-full max-w-md p-10 bg-white border" style={{ borderColor: 'var(--border)' }}>
        <Link to="/" className="font-display text-3xl block mb-2" data-testid="admin-brand">STILET</Link>
        <div className="overline mb-8">{t("admin.login_title", lang)}</div>
        <label className="field-label">Email</label>
        <input className="input-base mb-4" type="email" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="admin-email" />
        <label className="field-label">{t("admin.password", lang)}</label>
        <input className="input-base mb-6" type="password" value={password} onChange={e=>setPassword(e.target.value)} required data-testid="admin-password" />
        {err && <div className="mb-4 text-sm" style={{ color: '#7A1F1F' }} data-testid="admin-error">{String(err)}</div>}
        <button className="btn-primary w-full justify-center" type="submit" disabled={loading} data-testid="admin-submit">
          {loading ? "..." : t("admin.sign_in", lang)}
        </button>
      </form>
    </div>
  );
}

function AdminShell({ children, tab }) {
  const { lang, user, logout } = useApp();
  if (!user) return <Navigate to="/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }} data-testid="admin-shell">
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="container-x py-5 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="font-display text-2xl">STILET</Link>
            <div className="overline" style={{ color: 'var(--text-muted)' }}>{t("admin.dashboard", lang)}</div>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/admin/apartments" className={`nav-link ${tab === 'apartments' ? 'active' : ''}`} data-testid="admin-tab-apartments">{t("admin.apartments", lang)}</Link>
            <Link to="/admin/bookings" className={`nav-link ${tab === 'bookings' ? 'active' : ''}`} data-testid="admin-tab-bookings">{t("admin.bookings", lang)}</Link>
            <button onClick={logout} className="btn-link" data-testid="admin-logout">{t("admin.logout", lang)}</button>
          </div>
        </div>
      </div>
      <div className="container-x py-10">{children}</div>
    </div>
  );
}

function useAuthedAxios() {
  const { token, API } = useApp();
  const h = { Authorization: `Bearer ${token}` };
  return { API, headers: h };
}

const emptyApt = {
  name: "", slug: "", short_description_en: "", short_description_sr: "",
  description_en: "", description_sr: "", location_name_en: "", location_name_sr: "",
  address: "", lat: 44.8176, lng: 20.4569, price_per_night: 100, currency: "EUR",
  rooms: 1, beds: 1, bathrooms: 1, max_guests: 2, size_sqm: 50,
  amenities: [], images: [], featured: false, rules_en: "", rules_sr: "",
};

export function AdminApartments() {
  const { API, headers } = useAuthedAxios();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/apartments`).then(r => setItems(r.data));
  // load() reads `headers` from a hook each render; we intentionally run once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const body = {
      ...editing,
      lat: parseFloat(editing.lat), lng: parseFloat(editing.lng),
      price_per_night: parseFloat(editing.price_per_night),
      rooms: parseInt(editing.rooms), beds: parseInt(editing.beds),
      bathrooms: parseInt(editing.bathrooms), max_guests: parseInt(editing.max_guests),
      size_sqm: parseInt(editing.size_sqm),
      amenities: typeof editing.amenities === "string" ? editing.amenities.split(",").map(s=>s.trim()).filter(Boolean) : editing.amenities,
      images: typeof editing.images === "string" ? editing.images.split("\n").map(s=>s.trim()).filter(Boolean) : editing.images,
    };
    if (editing.id) {
      await axios.put(`${API}/admin/apartments/${editing.id}`, body, { headers });
    } else {
      await axios.post(`${API}/admin/apartments`, body, { headers });
    }
    setEditing(null); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(`${API}/admin/apartments/${id}`, { headers });
    load();
  };

  return (
    <AdminShell tab="apartments">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-3xl">Apartments</h2>
        <button className="btn-primary" onClick={() => setEditing({ ...emptyApt, amenities: "", images: "" })} data-testid="admin-add-apartment">+ New</button>
      </div>

      <table className="admin-table" data-testid="apartments-table">
        <thead><tr><th>Name</th><th>Slug</th><th>Location</th><th>Price</th><th>Featured</th><th></th></tr></thead>
        <tbody>
          {items.map(a => (
            <tr key={a.id} data-testid={`row-${a.slug}`}>
              <td>{a.name}</td><td>{a.slug}</td><td>{a.location_name_en}</td><td>€{a.price_per_night}</td>
              <td>{a.featured ? "Yes" : "No"}</td>
              <td className="space-x-3">
                <button className="btn-link" onClick={() => setEditing({
                  ...a,
                  amenities: (a.amenities || []).join(", "),
                  images: (a.images || []).join("\n"),
                })} data-testid={`edit-${a.slug}`}>Edit</button>
                <button className="btn-link" onClick={() => remove(a.id)} data-testid={`delete-${a.slug}`}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="edit-modal">
          <form onSubmit={save} className="bg-white max-w-3xl w-full max-h-[90vh] overflow-auto p-8">
            <h3 className="font-display text-2xl mb-6">{editing.id ? "Edit" : "New"} apartment</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["name", "Name"], ["slug", "Slug"],
                ["location_name_en", "Location EN"], ["location_name_sr", "Location SR"],
                ["address", "Address"], ["lat", "Lat"], ["lng", "Lng"],
                ["price_per_night", "Price/night"], ["rooms", "Rooms"], ["beds", "Beds"],
                ["bathrooms", "Bathrooms"], ["max_guests", "Max guests"], ["size_sqm", "Size m²"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="field-label">{label}</label>
                  <input className="input-base" value={editing[k]} onChange={e => setEditing({...editing, [k]: e.target.value})} required={["name","slug","lat","lng","price_per_night"].includes(k)} data-testid={`f-${k}`} />
                </div>
              ))}
              {[
                ["short_description_en", "Short EN"], ["short_description_sr", "Short SR"],
                ["description_en", "Description EN"], ["description_sr", "Description SR"],
                ["rules_en", "Rules EN"], ["rules_sr", "Rules SR"],
              ].map(([k, label]) => (
                <div key={k} className="col-span-2">
                  <label className="field-label">{label}</label>
                  <textarea className="input-base" rows={2} value={editing[k]} onChange={e => setEditing({...editing, [k]: e.target.value})} data-testid={`f-${k}`} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="field-label">Amenities (comma separated)</label>
                <input className="input-base" value={editing.amenities} onChange={e => setEditing({...editing, amenities: e.target.value})} data-testid="f-amenities" />
              </div>
              <div className="col-span-2">
                <label className="field-label">Images (one URL per line)</label>
                <textarea className="input-base" rows={3} value={editing.images} onChange={e => setEditing({...editing, images: e.target.value})} data-testid="f-images" />
              </div>
              <label className="flex items-center gap-2 col-span-2">
                <input type="checkbox" checked={editing.featured} onChange={e => setEditing({...editing, featured: e.target.checked})} data-testid="f-featured" />
                <span>Featured on homepage</span>
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="submit" className="btn-primary" data-testid="modal-save">Save</button>
              <button type="button" className="btn-outline" onClick={() => setEditing(null)} data-testid="modal-cancel">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </AdminShell>
  );
}

export function AdminBookings() {
  const { API, headers } = useAuthedAxios();
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/admin/bookings`, { headers }).then(r => setItems(r.data));
  // Intentional: load once on mount; load() closes over a stable axios call.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(`${API}/admin/bookings/${id}`, { headers });
    load();
  };

  return (
    <AdminShell tab="bookings">
      <div className="mb-6">
        <h2 className="font-display text-3xl">Bookings</h2>
        <p style={{ color: 'var(--text-muted)' }}>{items.length} total</p>
      </div>
      <table className="admin-table" data-testid="bookings-table">
        <thead><tr><th>Date</th><th>Apartment</th><th>Guest</th><th>Dates</th><th>Total</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {items.map(b => (
            <tr key={b.id} data-testid={`booking-row-${b.id}`}>
              <td>{new Date(b.created_at).toLocaleDateString()}</td>
              <td>{b.apartment_name}</td>
              <td>{b.full_name}<br/><span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{b.email}</span></td>
              <td>{b.check_in} → {b.check_out}<br/><span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{b.guests} guests</span></td>
              <td>€{b.total_price}</td>
              <td><span className={`status-pill status-${b.status}`}>{b.status}</span></td>
              <td className="space-x-2">
                {b.status !== "confirmed" && <button className="btn-link" onClick={() => updateStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`}>Confirm</button>}
                {b.status !== "cancelled" && <button className="btn-link" onClick={() => updateStatus(b.id, "cancelled")} data-testid={`cancel-${b.id}`}>Cancel</button>}
                <button className="btn-link" onClick={() => remove(b.id)} data-testid={`delete-booking-${b.id}`}>Delete</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={7} style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>No bookings yet</td></tr>}
        </tbody>
      </table>
    </AdminShell>
  );
}

export function AdminDashboard() {
  return <Navigate to="/admin/apartments" replace />;
}
